import { useState, useEffect, type FormEvent } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { authClient } from '../../services/authClient'
import { ApiClientError } from '../../services/apiClient'
import { useAuth } from '../../auth/AuthContext'
import { AuthShell } from './LoginPage'
import { Modal } from '../../components/Modal'
import { legalPublicClient, type LegalType, LEGAL_TYPE_LABELS } from '../../services/adminClient'

type LocationState = {
  verifiedEmailToken?: string
  email?: string
}

export function RegisterPage() {
  const { state, acceptTokenPair } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const navState = (location.state ?? {}) as LocationState
  const verifiedEmailToken = navState.verifiedEmailToken ?? ''
  const email = navState.email ?? ''

  const [password, setPassword] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastPaternal, setLastPaternal] = useState('')
  const [lastMaternal, setLastMaternal] = useState('')
  const [acceptedLegal, setAcceptedLegal] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const [legalModal, setLegalModal] = useState<LegalType | null>(null)
  const [legalHtml, setLegalHtml] = useState('')
  const [legalLoading, setLegalLoading] = useState(false)
  const [legalError, setLegalError] = useState<string | null>(null)

  useEffect(() => {
    if (!legalModal) return
    setLegalHtml('')
    setLegalError(null)
    setLegalLoading(true)
    legalPublicClient
      .current(legalModal)
      .then((res) => setLegalHtml(res.document.content_html))
      .catch(() => setLegalError('No se pudo cargar el documento. Intenta más tarde.'))
      .finally(() => setLegalLoading(false))
  }, [legalModal])

  if (state.status === 'authenticated') {
    return <Navigate to={state.user.audience === 'admin' ? '/admin' : '/dashboard'} replace />
  }

  // Si llegaron sin pasar por /verify-otp, redirigir al inicio del flujo.
  if (!verifiedEmailToken || !email) {
    return <Navigate to="/register" replace />
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setFieldErrors({})
    setSubmitting(true)
    try {
      const result = await authClient.registerComplete({
        verified_email_token: verifiedEmailToken,
        password,
        first_name: firstName,
        last_name_paternal: lastPaternal,
        last_name_maternal: lastMaternal,
        accepted_legal: acceptedLegal,
      })
      acceptTokenPair(result)
      navigate('/dashboard', { replace: true })
    } catch (err) {
      if (err instanceof ApiClientError) {
        const fields = err.apiError.details?.fields
        if (fields && typeof fields === 'object') {
          setFieldErrors(fields as Record<string, string>)
        }
        setError(err.apiError.message)
      } else {
        setError('No se pudo completar el registro. Verifica tu conexión.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <AuthShell title="Completa tu cuenta" subtitle="Ingresa tus datos para finalizar el registro">
        <form onSubmit={handleSubmit} className="auth-form" noValidate>
          <div className="field-row">
            <label className="field">
              <span>Nombre(s)</span>
              <input value={firstName} onChange={(e) => setFirstName(e.target.value)} required disabled={submitting} />
              {fieldErrors.first_name && <small className="field-error">{fieldErrors.first_name}</small>}
            </label>
          </div>

          <div className="field-row">
            <label className="field">
              <span>Apellido paterno</span>
              <input value={lastPaternal} onChange={(e) => setLastPaternal(e.target.value)} required disabled={submitting} />
              {fieldErrors.last_name_paternal && <small className="field-error">{fieldErrors.last_name_paternal}</small>}
            </label>
            <label className="field">
              <span>Apellido materno</span>
              <input value={lastMaternal} onChange={(e) => setLastMaternal(e.target.value)} required disabled={submitting} />
            </label>
          </div>

          <label className="field">
            <span>Correo electrónico</span>
            <input
              type="email"
              value={email}
              readOnly
              disabled
              autoComplete="email"
            />
          </label>

          <label className="field">
            <span>Contraseña</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
              disabled={submitting}
            />
            <small className="muted">Mínimo 8 caracteres con mayúscula, minúscula y número.</small>
            {fieldErrors.password && <small className="field-error">{fieldErrors.password}</small>}
          </label>

          <label className="checkbox">
            <input
              type="checkbox"
              checked={acceptedLegal}
              onChange={(e) => setAcceptedLegal(e.target.checked)}
              disabled={submitting}
            />
            <span>
              Acepto los{' '}
              <button type="button" className="link-btn" onClick={() => setLegalModal('TOS')}>
                Términos de Servicio
              </button>
              {' '}y el{' '}
              <button type="button" className="link-btn" onClick={() => setLegalModal('PP')}>
                Aviso de Privacidad
              </button>.
            </span>
          </label>
          {fieldErrors.accepted_legal && <small className="field-error">{fieldErrors.accepted_legal}</small>}

          {error && !Object.keys(fieldErrors).length && <p className="error">{error}</p>}

          <button type="submit" className="primary-btn" disabled={submitting}>
            {submitting ? 'Creando cuenta…' : 'Crear cuenta'}
          </button>

          <div className="auth-links">
            <Link to="/login">¿Ya tienes cuenta? Inicia sesión</Link>
          </div>
        </form>
      </AuthShell>

      <Modal
        open={legalModal !== null}
        onClose={() => setLegalModal(null)}
        title={legalModal ? LEGAL_TYPE_LABELS[legalModal] : ''}
        size="lg"
      >
        {legalLoading && <p className="muted">Cargando…</p>}
        {legalError && <p className="error">{legalError}</p>}
        {!legalLoading && !legalError && (
          <div className="legal-content" dangerouslySetInnerHTML={{ __html: legalHtml }} />
        )}
      </Modal>
    </>
  )
}
